import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ShoppingCartOutlined, SearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Spin, message } from 'antd';
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom'

import "../categories-explore/style.css";

import BreadcrumbComponent from "../../common/BreadcrumbComponent";
import FilterComponent from "../../common/FilterComponent"
import { fetchData, postData } from '../../../services/HomeService';
import ProductView from '../ProductView';
import { SPIN_TEXT_MAP, COMPONENT_ROUTE_MAP } from '../../../constants';

// const _SEGMENT_LIST = ["economical", "value-added", "premium", "super-premium"];

export default function LocalitiesExplore({ selectedParent, selectedChild, AvatarColor, feeds }) {

    const observerRef = useRef();
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    
    const [productList, setProductList] = useState([]);
    const [inBasketCount, setInBasketCount] = useState(0);
    const [canLoadMore, setCanLoadMore] = useState(true);
    const [currPageInfo, setCurrPageInfo] = useState({ currTab: 0, currPage: 1 });
    
    // explore options
    const [superCategories, setSuperCategories] = useState([]);
    const [superCategory, setSuperCategory] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(new Set());
    const [selectedCity, setSelectedCity] = useState([]);
    const [selectedSegment, setSelectedSegment] = useState([]);
    
    const [showFilter, setShowFilter] = useState(false);
    const [tagFilter, setTagFilter] = useState('');
    const [showProfiles, setShowProfiles] = useState(false);

    const userData = useSelector((state) => state.user);
    const navigate = useNavigate();
    
    const getProductList = useCallback(async (scrollAction=false) => {
        if (currPageInfo.currPage <= 0 || feeds.length === 0 || !canLoadMore || loading) return;

        setLoading(true);
        const currTab = currPageInfo.currTab;

        const categoriesFilter = selectedCategory.size ? `&categories=${Array.from(selectedCategory).join(',')}` : '';
        const cityFilter = superCategory.name ? `&location=${superCategory.name}` : '';
        const segmentFilter = selectedSegment.length ? `&segments=${selectedSegment.join(',')}` : '';

        const res = await fetchData(`${feeds[currTab].api.path}${categoriesFilter}${cityFilter}${segmentFilter}&limit=10&page=${currPageInfo.currPage}`);

        if (!res) {
            setLoading(false);
            return;
        }

        if (res.data?.length < 10) {
            setCanLoadMore(false);
        }

        if (res.data?.length && scrollAction) {
            setProductList(prev => [...prev, ...res.data]);
            setCurrPageInfo(prev => ({ ...prev, currPage: prev.currPage + 1 }));
        }else{
            setProductList(res.data);
        }

        setInBasketCount(res.meta?.totalInBasket)
        setLoading(false);
        setShowProfiles(true);
    }, [currPageInfo, selectedCategory, canLoadMore, loading]);

    const handleReactProduct = async (product) => {
        const payload = {
            action: product.isLiked ? 'dislike' : 'like',
            actor: userData.id
        }

        const res = await postData(`/products/react/${product._id}`, payload);
        if (res !== null) {
            const tmpProductList = productList.map((val) => {
                if (val._id === product._id) 
                    return {
                        ...val,
                        isLiked: !product.isLiked,
                        totalLikes: (product?.totalLikes || 0) + (product.isLiked ? -1 : 1)
                    }
                return val;
            });

            setProductList(tmpProductList);
        }
    }

    const handleShopProduct = async (product) => {
        const payload = {
            action: product.inBasket ? 'remove' : 'add',
            actor: userData.id
        }

        const res = await postData(`/products/shop/${product._id}`, payload);
        if (res?.data) {
            const tmpProductList = productList.map((val) => {
                if (val._id === product._id) 
                    return {
                        ...val,
                        inBasket: !product.inBasket,
                    }
                return val;
            });

            setProductList(tmpProductList);
            setInBasketCount(inBasketCount + (!product.inBasket ? 1: -1))
            message.success(SPIN_TEXT_MAP[!product.inBasket ? 'PRODUCT_IN_BASKET' : 'PRODUCT_NOTIN_BASKET'], 2);
        } else {
            message.error(res?.message || "Some error happened!!", 2);
        }
    }

    const handleViewBasket = () => {
        navigate(`/home/${COMPONENT_ROUTE_MAP['ProductListing']}?tab=1&title=${superCategory.name} Branch`)
    }

    const handleSearch = () => {
        // search using input
    }

    const handleCategoryChange = (value) => {
        setCanLoadMore(true)
        setCurrPageInfo(prev => ({ ...prev, currPage: 1 }))
        if (value === "all") {
            setSelectedCategory(new Set());
            return;
        } else if(value === "reset"){
            setSelectedCategory(new Set(categoryOptions.map(val => val.name)));
            return;
        }
        const newSelectedCategory = new Set(selectedCategory);
        newSelectedCategory.delete(value);
        setSelectedCategory(newSelectedCategory);
    };

    const handleCityChange = (value) => {
        setSelectedCity(value);
    };

    const handleSegmentChange = (value) => {
        setCanLoadMore(true)
        setCurrPageInfo(prev => ({ ...prev, currPage: 1 }))
        setSelectedSegment(value);
    };

    const handleCategoryClick = (category) => () => {
        setSuperCategory(category);
        setTagFilter(category.filter);
        setCategoryOptions(category.categories);
        setSelectedCategory(new Set(category.categories?.map(val => val.name)));
        setShowProfiles(true);
        setShowFilter(true);
    };


    const handleObserve = useCallback((entries) => {
        const [entry] = entries;
        // Only trigger if actually intersecting and not during fast scrolls
        if (entry.isIntersecting && !loading && canLoadMore && entry.intersectionRatio >= 0.1) {
            getProductList(true);
        }
    }, [getProductList, loading, canLoadMore]);

    const lastPostElementRef = useCallback(
        (node) => {
            if (loading) return;
            
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            
            if (node && canLoadMore) {
                observerRef.current = new IntersectionObserver(handleObserve, { 
                    root: null,
                    rootMargin: '0px',
                    threshold: 0.1
                });
                observerRef.current.observe(node);
            }
        },
        [loading, canLoadMore, handleObserve]
    );

    useEffect(() => {
        if (selectedCategory.size) getProductList();
    }, [selectedCategory, selectedCity, selectedSegment]);

    useEffect(() => {
        const fetchSuperCategories = async () => {
            try {
                const response = await fetchData(`/categories/tags/location-feed`);
                setSuperCategories(response);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };

        fetchSuperCategories();
    }, []);

    const onSearch = async () => {
    }

    return (
        <section className="Home_container">
            <section className="Home_firstContainer">
                <BreadcrumbComponent
                    selectedParent={selectedParent}
                    selectedChild={selectedChild}
                />
                <div className="Home_heading" >
                    {
                        !showProfiles ? <h1>{selectedChild}</h1> : (
                            <>
                                <Button
                                    type="link"
                                    style={{ padding: 0, height: "28px" }}
                                    onClick={() => {
                                        setSuperCategory(null);
                                        setCanLoadMore(true)
                                        setShowProfiles(false);
                                    }}
                                >
                                    <ArrowLeftOutlined />
                                </Button>
                                <h1><b>{superCategory.label}</b></h1>
                                <Button type="primary" style={{"marginLeft": "auto"}} onClick={handleViewBasket}>
                                    <ShoppingCartOutlined /> {`View Basket ${inBasketCount ? `(${inBasketCount})` : ''}`}
                                </Button>
                            </>
                        )
                    }
                    
                </div>
            </section>
            <main className='VendorOnboard_adding-2' style={{ flexDirection: "column" }}>
                {!showProfiles ? (
                    <div className="categories-container" style={{ width: "100%" }}>
                        <Row gutter={[16, 16]}>
                            {superCategories.map(category => (
                                <Col key={category.id} xs={24} sm={12} md={8}>
                                    <div className="category-item" style={{ backgroundImage: `url(${category.image})`, backgroundSize: 'cover' }} onClick={handleCategoryClick(category)}>
                                        <div className="category-overlay">
                                            <h2>{category.label}</h2>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ) : (
                    <div style={{width: "100%", maxWidth:"1050px"}}>
                        {
                            showFilter && (
                                <FilterComponent 
                                    type={"operation"}
                                    categories={selectedCategory}
                                    selectedCity={selectedCity}
                                    selectedSegment={selectedSegment}
                                    onCityChange={handleCityChange}
                                    onSegmentChange={handleSegmentChange}
                                    onCategoryChange={handleCategoryChange}
                                >
                                </FilterComponent>
                            )
                        }
                        <div className="profiles-container" style={{ width: "100%" }}>
                            <Row gutter={[16, 16]}>
                                <Col lg={{ span: 6 }} md={{ span: 24 }}>
                                </Col>
                                <Col lg={{ span: 12 }} md={{ span: 24 }}>
                                    <Form onSubmitCapture={handleSearch}>
                                        <Form.Item style={{ marginBottom: 10 }}>
                                            <div className={"searchBox"}>
                                                <Input
                                                    placeholder='Search something'
                                                    style={{ borderRadius: '28px' }}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                <SearchOutlined
                                                    className={"searchIcon"}
                                                    onClick={handleSearch}
                                                />
                                            </div>
                                        </Form.Item>
                                    </Form>

                                    {
                                        productList.map((val, i) => (
                                            <ProductView
                                                key={`productlist-${i}`}
                                                handleReactProduct={handleReactProduct}
                                                handleShopProduct={handleShopProduct}
                                                product={val}
                                                ref={i === productList.length - 1 ? lastPostElementRef : null}
                                            />
                                        ))
                                    }
                                    {loading && (
                                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                                            <Spin />
                                        </div>
                                    )}
                                    {!canLoadMore && (
                                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                                            <p>No more products to display</p>
                                        </div>
                                    )}
                                </Col>
                                <Col lg={{ span: 6 }} md={{ span: 24 }}>

                                </Col>
                            </Row>
                        </div>
                    </div>
                )}
            </main>
        </section>
    );
}
